import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { navLinks } from "../../config/navLinks";
import { portfolioEnv } from "../../config/portfolioEnv";
import { getInitials } from "../../lib/getInitials";

const desktopNavLink = ({ isActive }: { isActive: boolean }) =>
	isActive
		? "rounded-lg bg-brand-accent/10 px-3 py-2 text-sm font-medium text-brand-accent"
		: "rounded-lg px-3 py-2 text-sm font-medium text-brand-muted transition-colors hover:text-brand-accent";

const mobileNavLink = ({ isActive }: { isActive: boolean }) =>
	isActive
		? "flex min-h-11 items-center rounded-lg bg-brand-accent/10 px-4 text-base font-medium text-brand-accent"
		: "flex min-h-11 items-center rounded-lg px-4 text-base font-medium text-brand-muted transition-colors hover:bg-surface-muted hover:text-brand-accent";

const iconButtonClass =
	"inline-flex items-center justify-center rounded-lg p-2 text-brand transition-colors hover:bg-surface-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-accent";

const AppNav = () => {
	const [menuOpen, setMenuOpen] = useState(false);
	const { name} = portfolioEnv;
	const initials = getInitials(name);

	const closeMenu = () => setMenuOpen(false);
	const toggleMenu = () => setMenuOpen((open) => !open);

	return (
		<header className='fixed top-0 left-0 z-20 w-full border-b border-surface-muted bg-surface-elevated/95 shadow-sm backdrop-blur-sm'>
			<nav
				className='mx-auto flex h-14 max-w-6xl items-center justify-between gap-4 px-4'
				aria-label='Main navigation'
			>
				<Link
					to='/home'
					className='flex shrink-0 items-center gap-2.5'
					onClick={closeMenu}
					aria-label={`${name} — home`}
				>
					<span className='flex h-9 w-9 items-center justify-center rounded-lg bg-brand font-display text-sm font-bold text-white'>
						{initials}
					</span>
				</Link>

				<ul className='hidden items-center gap-1 md:flex'>
					{navLinks.map(({ to, label, end }) => (
						<li key={to}>
							<NavLink to={to} end={end} className={desktopNavLink}>
								{label}
							</NavLink>
						</li>
					))}
				</ul>

				<div className='flex items-center gap-1'>
					<button
						type='button'
						className={`${iconButtonClass} md:hidden`}
						aria-controls='main-nav-links'
						aria-expanded={menuOpen}
						aria-label={menuOpen ? "Close menu" : "Open menu"}
						onClick={toggleMenu}
					>
						{menuOpen ? (
							<svg
								xmlns='http://www.w3.org/2000/svg'
								fill='none'
								viewBox='0 0 24 24'
								strokeWidth={1.5}
								stroke='currentColor'
								className='h-6 w-6'
								aria-hidden
							>
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									d='M6 18L18 6M6 6l12 12'
								/>
							</svg>
						) : (
							<svg
								xmlns='http://www.w3.org/2000/svg'
								fill='none'
								viewBox='0 0 24 24'
								strokeWidth={1.5}
								stroke='currentColor'
								className='h-6 w-6'
								aria-hidden
							>
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									d='M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5'
								/>
							</svg>
						)}
					</button>
				</div>
			</nav>

			<div
				id='main-nav-links'
				className={`border-b border-surface-muted bg-surface-elevated md:hidden ${
					menuOpen ? "block" : "hidden"
				}`}
			>
				<ul className='mx-auto max-w-6xl space-y-1 px-4 py-3'>
					{navLinks.map(({ to, label, end }) => (
						<li key={to}>
							<NavLink
								to={to}
								end={end}
								className={mobileNavLink}
								onClick={closeMenu}
							>
								{label}
							</NavLink>
						</li>
					))}
				</ul>
			</div>
		</header>
	);
};

export default AppNav;
